import React, { useState, useRef, useEffect } from "react";
import TextareaAutosize from "react-textarea-autosize";
import { CanadianButtonSmall } from "../ui-components/CanadianButton";
import { SERVER_URL } from "../api/client";
import { GENERATE_VIDEO, REMIX_VIDEO } from "../api/paths";
import Spinner from "../components/Spinner";
import {
  IonContent,
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
} from "@ionic/react";
import toast from "react-hot-toast";
import { FiDownload, FiX } from "react-icons/fi";
import { Helmet } from "react-helmet-async";

const VideoGenerator = () => {
  const [prompt, setPrompt] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [loading, setLoading] = useState(false);
  const [videoUrl, setVideoUrl] = useState(null);
  const [error, setError] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [videoId, setVideoId] = useState(null);
  const [remixPrompt, setRemixPrompt] = useState("");
  const [remixLoading, setRemixLoading] = useState(false);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const remixTextareaRef = useRef(null);

  // Cleanup blob URLs on unmount
  useEffect(() => {
    return () => {
      if (videoUrl && videoUrl.startsWith("blob:")) {
        URL.revokeObjectURL(videoUrl);
      }
      if (imagePreview && imagePreview.startsWith("blob:")) {
        URL.revokeObjectURL(imagePreview);
      }
    };
  }, [videoUrl, imagePreview]);

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Please select a JPEG, PNG, or WebP image");
      return;
    }

    // Cleanup previous preview
    if (imagePreview && imagePreview.startsWith("blob:")) {
      URL.revokeObjectURL(imagePreview);
    }

    setSelectedImage(file);
    const previewUrl = URL.createObjectURL(file);
    setImagePreview(previewUrl);
  };

  const handleRemoveImage = () => {
    if (imagePreview && imagePreview.startsWith("blob:")) {
      URL.revokeObjectURL(imagePreview);
    }
    setSelectedImage(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleGenerateVideo = async () => {
    if (!prompt.trim()) {
      toast.error("Please enter a prompt to generate a video");
      return;
    }

    if (!apiKey.trim()) {
      toast.error("Please enter your OpenAI API key");
      return;
    }

    // Cleanup previous blob URL if exists
    if (videoUrl && videoUrl.startsWith("blob:")) {
      URL.revokeObjectURL(videoUrl);
    }

    setLoading(true);
    setError(null);
    setVideoUrl(null);

    try {
      // Create FormData for multipart/form-data request
      const formData = new FormData();
      formData.append("prompt", prompt.trim());
      formData.append("apiKey", apiKey.trim());

      // Add image if selected
      if (selectedImage) {
        formData.append("input_reference", selectedImage);
      }

      const response = await fetch(SERVER_URL + "/" + GENERATE_VIDEO, {
        method: "POST",
        credentials: "include",
        body: formData,
      });

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 403) {
          throw new Error("Access denied. Admin privileges required.");
        }
        throw new Error(data.message || "Failed to generate video");
      }

      if (data.success && data.videoData) {
        // Convert base64 to blob URL
        const binaryString = atob(data.videoData);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        const blob = new Blob([bytes], { type: data.mimeType || "video/mp4" });
        const blobUrl = URL.createObjectURL(blob);

        setVideoUrl(blobUrl);
        // Store videoId for remix functionality
        if (data.videoId) {
          setVideoId(data.videoId);
        }
        toast.success("Video generated successfully!");
      } else if (data.success && data.videoUrl) {
        // Fallback for direct URL if provided
        setVideoUrl(data.videoUrl);
        if (data.videoId) {
          setVideoId(data.videoId);
        }
        toast.success("Video generated successfully!");
      } else {
        throw new Error("No video data received");
      }
    } catch (err) {
      console.error("Error generating video:", err);
      setError(err.message);
      toast.error(err.message || "Failed to generate video");
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleGenerateVideo();
    }
  };

  const handleDownload = async () => {
    if (!videoUrl) return;

    try {
      // Create download link
      const a = document.createElement("a");
      a.href = videoUrl;
      a.download = `video-${Date.now()}.mp4`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (err) {
      console.error("Error downloading video:", err);
      toast.error("Failed to download video");
    }
  };

  const handleRemixVideo = async () => {
    if (!remixPrompt.trim()) {
      toast.error("Please enter a remix prompt");
      return;
    }

    if (!videoId) {
      toast.error("No video ID available for remix");
      return;
    }

    if (!apiKey.trim()) {
      toast.error("Please enter your OpenAI API key");
      return;
    }

    setRemixLoading(true);
    setError(null);
    // Keep the current video visible while remixing

    try {
      const response = await fetch(SERVER_URL + "/" + REMIX_VIDEO, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          videoId: videoId,
          prompt: remixPrompt.trim(),
          apiKey: apiKey.trim(),
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 403) {
          throw new Error("Access denied. Admin privileges required.");
        }
        throw new Error(data.message || "Failed to remix video");
      }

      if (data.success && data.videoData) {
        // Cleanup previous blob URL if exists (after we have the new one)
        if (videoUrl && videoUrl.startsWith("blob:")) {
          URL.revokeObjectURL(videoUrl);
        }

        // Convert base64 to blob URL
        const binaryString = atob(data.videoData);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        const blob = new Blob([bytes], { type: data.mimeType || "video/mp4" });
        const blobUrl = URL.createObjectURL(blob);

        setVideoUrl(blobUrl);
        // Update videoId with the new remixed video ID
        if (data.videoId) {
          setVideoId(data.videoId);
        }
        toast.success("Video remixed successfully!");
      } else {
        throw new Error("No video data received");
      }
    } catch (err) {
      console.error("Error remixing video:", err);
      setError(err.message);
      toast.error(err.message || "Failed to remix video");
    } finally {
      setRemixLoading(false);
    }
  };

  const handleRemixKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleRemixVideo();
    }
  };

  return (
    <>
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <IonPage>
        <IonHeader mode="ios">
          <IonToolbar>
            <IonTitle>LoonVid</IonTitle>
          </IonToolbar>
        </IonHeader>
        <IonContent className="ion-padding">
          <div className="video-generator">
            <div className="video-generator__header-image">
              <img src="/loonvid.jpeg" alt="LoonVid" />
            </div>
            <div className="video-generator__content">
              {/* Input Section */}
              <div className="video-generator__input-section">
                <label htmlFor="api-key" className="video-generator__label">
                  LL-Code
                </label>
                <input
                  id="api-key"
                  type="password"
                  className="video-generator__input"
                  placeholder="LL-Code..."
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  disabled={loading}
                />

                <label
                  htmlFor="video-prompt"
                  className="video-generator__label"
                  style={{ marginTop: "1rem" }}
                >
                  Describe your video
                </label>
                <TextareaAutosize
                  ref={textareaRef}
                  id="video-prompt"
                  className="video-generator__textarea"
                  placeholder="Describe your video..."
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  onKeyPress={handleKeyPress}
                  disabled={loading}
                  minRows={4}
                  maxRows={10}
                />

                <label
                  htmlFor="input-image"
                  className="video-generator__label"
                  style={{ marginTop: "1rem" }}
                >
                  Reference Image (720x1280)
                </label>
                <div style={{ marginTop: "0.5rem" }}>
                  {!imagePreview ? (
                    <label
                      htmlFor="input-image"
                      style={{
                        display: "inline-block",
                        padding: "0.75rem 1rem",
                        border: "2px dashed #666",
                        borderRadius: "8px",
                        cursor: "pointer",
                        textAlign: "center",
                        width: "100%",
                      }}
                    >
                      <input
                        ref={fileInputRef}
                        id="input-image"
                        type="file"
                        accept="image/jpeg,image/jpg,image/png,image/webp"
                        onChange={handleImageSelect}
                        disabled={loading}
                        style={{ display: "none" }}
                      />
                      <span style={{ color: "#666" }}>
                        Click to upload image (JPEG, PNG, or WebP)
                      </span>
                    </label>
                  ) : (
                    <div
                      style={{
                        position: "relative",
                        display: "inline-block",
                        width: "100%",
                        maxWidth: "300px",
                      }}
                    >
                      <img
                        src={imagePreview}
                        alt="Preview"
                        style={{
                          width: "100%",
                          height: "auto",
                          borderRadius: "8px",
                        }}
                      />
                      <button
                        type="button"
                        onClick={handleRemoveImage}
                        disabled={loading}
                        style={{
                          position: "absolute",
                          top: "8px",
                          right: "8px",
                          background: "rgba(0, 0, 0, 0.7)",
                          color: "white",
                          border: "none",
                          borderRadius: "50%",
                          width: "28px",
                          height: "28px",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          transition: "background 0.2s",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background =
                            "rgba(0, 0, 0, 0.9)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background =
                            "rgba(0, 0, 0, 0.7)";
                        }}
                      >
                        <FiX size={16} />
                      </button>
                    </div>
                  )}
                </div>

                <div className="video-generator__actions">
                  <CanadianButtonSmall
                    onClick={handleGenerateVideo}
                    disabled={loading || !prompt.trim() || !apiKey.trim()}
                  >
                    {loading ? "Generating..." : "Generate Video"}
                  </CanadianButtonSmall>
                </div>
              </div>

              {/* Loading State */}
              {loading && (
                <div className="video-generator__loading">
                  <Spinner />
                  <p>Generating your video... This may take a moment.</p>
                </div>
              )}

              {/* Error State */}
              {error && (
                <div className="video-generator__error">
                  <p>⚠️ {error}</p>
                </div>
              )}

              {/* Video Result */}
              {videoUrl && !loading && (
                <div className="video-generator__result">
                  <div className="video-generator__result-header">
                    <button
                      className="video-generator__download-btn"
                      onClick={handleDownload}
                      title="Download Video"
                    >
                      <FiDownload size={20} />
                      Download
                    </button>
                  </div>
                  <div className="video-generator__video-container">
                    <video
                      src={videoUrl}
                      controls
                      autoPlay
                      loop
                      className="video-generator__video"
                    >
                      Your browser does not support the video tag.
                    </video>
                  </div>

                  {/* Remix Section */}
                  {videoId && (
                    <div
                      style={{
                        marginTop: "1.5rem",
                        padding: "1rem",
                        border: "1px solid #ddd",
                        borderRadius: "8px",
                        backgroundColor: "#f9f9f9",
                      }}
                    >
                      <label
                        htmlFor="remix-prompt"
                        className="video-generator__label"
                      >
                        Remix this video
                      </label>
                      <TextareaAutosize
                        ref={remixTextareaRef}
                        id="remix-prompt"
                        className="video-generator__textarea"
                        placeholder="Describe your changes"
                        value={remixPrompt}
                        onChange={(e) => setRemixPrompt(e.target.value)}
                        onKeyPress={handleRemixKeyPress}
                        disabled={remixLoading}
                        minRows={3}
                        maxRows={6}
                        style={{ marginTop: "0.5rem" }}
                      />
                      <div
                        className="video-generator__actions"
                        style={{ marginTop: "0.75rem" }}
                      >
                        <CanadianButtonSmall
                          onClick={handleRemixVideo}
                          disabled={
                            remixLoading ||
                            !remixPrompt.trim() ||
                            !apiKey.trim()
                          }
                        >
                          {remixLoading ? "Remixing..." : "Remix Video"}
                        </CanadianButtonSmall>
                      </div>
                      {remixLoading && (
                        <div className="video-generator__loading">
                          <Spinner />
                          <p>Remixing your video...</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </IonContent>
      </IonPage>
    </>
  );
};

export default VideoGenerator;
